import {
  GetIssueResponseDto,
  IssueTimelineDto,
  IssueTimelineDtoEventTypeEnum,
} from '@issuemanager/api-client';
import { ExtendedTypography } from '@issuemanager/apps/client/components';
import { useUserDateRenderer } from '@issuemanager/apps/client/hooks';
import { Box, Stack } from '@mui/material';
import ViewIssueAvatar from '../../../issue/components/user/ViewIssueAvatar';
import ViewIssueUserName from '../../../issue/components/user/ViewIssueUserName';
import { IssueTimelineEvent } from './issue-timeline-event';

export interface IssueTimelineProps {
  timeline?: GetIssueResponseDto['timelineEvents'];
}

type GroupedTimelineEvents = {
  issueEdited: Record<number, IssueTimelineDto[]>;
  actionEdited: Record<string, IssueTimelineDto[]>;
};

export function IssueTimeline(props: IssueTimelineProps) {
  const { timeline = [] } = props;

  const dateRenderer = useUserDateRenderer();

  if (!timeline.length) {
    return null;
  }

  const groupedEvents = timeline.reduce(
    (acc: GroupedTimelineEvents, event) => {
      const eventDate = new Date(event.eventAt).getTime();

      if (event.eventType === IssueTimelineDtoEventTypeEnum.IssueEdited) {
        if (!acc.issueEdited[eventDate]) {
          acc.issueEdited[eventDate] = [];
        }
        acc.issueEdited[eventDate].push(event);
      } else if (
        event.eventType === IssueTimelineDtoEventTypeEnum.ActionEdited
      ) {
        // TODO: Action title is going to be mandatory.
        // Delete this check after the API & Model is updated.
        const actionTitle = event.bodyData.parameters.actionTitle?.value || '';
        const groupKey = `${eventDate}-${actionTitle}`;

        if (!acc.actionEdited[groupKey]) {
          acc.actionEdited[groupKey] = [];
        }

        acc.actionEdited[groupKey].push(event);
      }

      return acc;
    },
    { issueEdited: {}, actionEdited: {} },
  );

  const timelineWithoutGroupedEvents = timeline.filter(
    (event) =>
      event.eventType !== IssueTimelineDtoEventTypeEnum.IssueEdited &&
      event.eventType !== IssueTimelineDtoEventTypeEnum.ActionEdited,
  );

  const allEvents = [
    ...timelineWithoutGroupedEvents,
    ...Object.entries(groupedEvents.issueEdited).map(([timestamp, events]) => ({
      id: `grouped-issue-edited-${timestamp}`,
      eventAt: events[0].eventAt,
      eventType: IssueTimelineDtoEventTypeEnum.IssueEdited,
      bodyData: events[0].bodyData,
      events,
    })),
    ...Object.entries(groupedEvents.actionEdited).map(([groupKey, events]) => ({
      id: `grouped-action-edited-${groupKey}`,
      actionTitle: events[0].bodyData.parameters.actionTitle?.value,
      eventAt: events[0].eventAt,
      eventType: IssueTimelineDtoEventTypeEnum.ActionEdited,
      bodyData: events[0].bodyData,
      events,
    })),
  ].sort(
    (a, b) => new Date(b.eventAt).getTime() - new Date(a.eventAt).getTime(),
  );

  return (
    <Stack spacing={1.5}>
      {allEvents.map((item) => {
        const isGroupedEvent = 'events' in item;

        return (
          <Stack
            key={item.id}
            direction='row'
            justifyContent='space-between'
            padding={1.5}
          >
            <Stack spacing={1.5} direction='row'>
              <ViewIssueAvatar
                name={item.bodyData.parameters.actorName.value}
                size='normal'
                initials={item.bodyData.parameters.actorInitials?.value}
              />

              <Stack spacing={1}>
                <ViewIssueUserName
                  name={item.bodyData.parameters.actorName.value}
                />

                <IssueTimelineEvent
                  data={isGroupedEvent ? item.events : item}
                />
              </Stack>
            </Stack>
            <Box>
              <ExtendedTypography variant='labelSm' noWrap>
                {dateRenderer.renderDateTime(item.eventAt)}
              </ExtendedTypography>
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
}
